import type { FC, ReactNode } from "react";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { useFieldContext } from "..";
import { FieldErrors } from "./FieldErrors";

interface SelectFieldProps {
	label?: string;
	tooltip?: string;
	hint?: string;
	size?: "sm" | "md";
	placeholder?: string;
	placeholderIcon?: FC | ReactNode;
	popoverClassName?: string;
	items?: SelectItemType[];
	children: ReactNode | ((item: SelectItemType) => ReactNode);
	// Additional props that might be needed for validation states
	isDisabled?: boolean;
	isRequired?: boolean;
	field?: any; // Simplified for now - will be properly typed later
	/** Whether to show supporting text in the selected value display. Defaults to true. */
	showSupportingTextInValue?: boolean;
	/** Callback fired when an item is selected, receives the selected key */
	onItemSelect?: (key: string | null) => void;
	/** Load more when user scrolls near bottom (infinite list) */
	onLoadMore?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	"data-testid"?: string;
}

const SelectField = ({
	label,
	tooltip,
	hint,
	size = "sm",
	placeholder = "Select",
	placeholderIcon,
	popoverClassName,
	items,
	children,
	isDisabled,
	isRequired,
	field: fieldProp,
	showSupportingTextInValue = true,
	onItemSelect,
	onLoadMore,
	hasNextPage,
	isFetchingNextPage,
	...props
}: SelectFieldProps) => {
	// Support both patterns:
	// 1. form.Field with explicit field prop
	// 2. form.AppField with useFieldContext
	const contextField = useFieldContext<string>();
	const field = fieldProp || contextField;
	const value = typeof field.state.value === "string" ? field.state.value : "";
	const showInvalid =
		(field.state.meta.isTouched || field.state.meta.isDirty) &&
		!field.state.meta.isValid;

	return (
		<div className="gap-2">
			<Select
				{...props}
				label={label}
				tooltip={tooltip}
				hint={hint}
				size={size}
				placeholder={placeholder}
				placeholderIcon={placeholderIcon}
				popoverClassName={popoverClassName}
				items={items}
				isDisabled={isDisabled || field.state.meta.isValidating}
				isInvalid={showInvalid}
				isRequired={isRequired || field.state.meta.isRequired}
				selectedKey={value.length > 0 ? value : null}
				onSelectionChange={(key) => {
					field.handleChange(key != null ? String(key) : "");
					onItemSelect?.(key as string | null);
				}}
				onBlur={field.handleBlur}
				name={field.name}
				showSupportingTextInValue={showSupportingTextInValue}
				onLoadMore={onLoadMore}
				hasNextPage={hasNextPage}
				isFetchingNextPage={isFetchingNextPage}
			>
				{children}
			</Select>
			<FieldErrors meta={field.state.meta} />
		</div>
	);
};

export default SelectField;
